import classNames from 'classnames';
import { useRef, useState } from 'react';
import { useCloseOnOutsideOrEscape } from '../../../hooks';
import ThemeControl from '../ThemeControl/ThemeControl';

export interface IGameHeaderProps {
  isChatOpen: boolean;
  isRulesOpen: boolean;
  hasUnreadMessages: boolean;
  toggleChat: () => void;
  toggleRules: () => void;
  leaveGame: () => void;
}

const FOCUS_CLASS_NAME =
  'outline-none focus-visible:ring-3 focus-visible:ring-accent-fill';

const ICON_BUTTON_CLASS_NAME = classNames(
  'relative flex size-[44px] items-center justify-center rounded-[12px] text-ink',
  FOCUS_CLASS_NAME
);

const CLOSE_ICON_PATH = 'M6 6l12 12M18 6L6 18';

interface IMenuItemProps {
  label: string;
  iconPath: string;
  className: string;
  onClick: () => void;
}

function MenuItem({ label, iconPath, className, onClick }: IMenuItemProps) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        'flex h-[44px] w-full items-center gap-[10px] rounded-[12px] bg-transparent px-[10px] hover:bg-control',
        className,
        FOCUS_CLASS_NAME
      )}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={iconPath} />
      </svg>
      <span className="text-[14px] font-semibold tracking-normal normal-case">
        {label}
      </span>
    </button>
  );
}

export default function GameHeader({
  isChatOpen,
  isRulesOpen,
  hasUnreadMessages,
  toggleChat,
  toggleRules,
  leaveGame,
}: IGameHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const showUnreadDot = hasUnreadMessages && !isChatOpen;
  const chatButtonLabel = isChatOpen
    ? 'Close chat'
    : showUnreadDot
      ? 'Chat, new messages'
      : 'Chat';

  useCloseOnOutsideOrEscape(menuRef, isMenuOpen, setIsMenuOpen);

  const leave = () => {
    setIsMenuOpen(false);
    leaveGame();
  };

  const openRules = () => {
    setIsMenuOpen(false);
    toggleRules();
  };

  return (
    <header className="relative z-30 flex h-[52px] shrink-0 items-center justify-between bg-header px-[6px] text-ink shadow-[inset_0_-1px_0_var(--color-hairline)]">
      <div ref={menuRef} className="relative">
        <button
          aria-label={isRulesOpen ? 'Close rules' : 'Game menu'}
          aria-expanded={isMenuOpen || isRulesOpen}
          onClick={isRulesOpen ? toggleRules : () => setIsMenuOpen(!isMenuOpen)}
          className={classNames(
            ICON_BUTTON_CLASS_NAME,
            isMenuOpen ? 'bg-control' : 'bg-transparent'
          )}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path
              d={isRulesOpen ? CLOSE_ICON_PATH : 'M4 7h16M4 12h16M4 17h16'}
            />
          </svg>
        </button>
        {isMenuOpen && (
          <div className="absolute top-full left-0 mt-[4px] w-[200px] rounded-[16px] bg-surface p-[6px] shadow-dock">
            <MenuItem
              label="Rules"
              iconPath="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2zM22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z"
              className="text-ink"
              onClick={openRules}
            />
            <MenuItem
              label="Leave game"
              iconPath="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M10 16l-4-4 4-4M6 12h10"
              className="text-danger"
              onClick={leave}
            />
            <div className="mt-[6px] border-t border-hairline pt-[8px]">
              <ThemeControl />
            </div>
          </div>
        )}
      </div>
      <span className="flex items-center gap-[6px] text-[16px] font-bold tracking-[-0.2px]">
        <img
          src="/limajuice.png"
          alt=""
          className="size-[32px] object-contain"
        />
        Tens &amp; Twos
      </span>
      <button
        aria-label={chatButtonLabel}
        aria-expanded={isChatOpen}
        onClick={toggleChat}
        className={classNames(ICON_BUTTON_CLASS_NAME, 'bg-transparent')}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path
            d={
              isChatOpen
                ? CLOSE_ICON_PATH
                : 'M20 11.5a8 8 0 0 1-11.7 7.1L4 20l1.3-4A8 8 0 1 1 20 11.5z'
            }
          />
        </svg>
        {showUnreadDot && (
          <span
            aria-hidden="true"
            className="absolute top-[10px] right-[9px] size-[9px] rounded-full bg-accent-ring shadow-[0_0_0_2px_var(--color-header)]"
          />
        )}
      </button>
    </header>
  );
}
